"use client"

import type React from "react"
import { useEffect, useRef } from "react"

interface RingEffect {
  x: number
  y: number
  startTime: number
  id: number
}

interface CursorClickEffectProps {
  /** Maximum diameter the ring expands to in px */
  size?: number
  /** Animation duration in ms */
  duration?: number
  /** CSS color string for the ring */
  color?: string
  /** Ring stroke width in px */
  strokeWidth?: number
  /** z-index */
  zIndex?: number
}

export const CursorClickEffect: React.FC<CursorClickEffectProps> = ({
  size = 50,
  duration = 600,
  color = "rgba(255, 255, 255, 0.8)",
  strokeWidth = 2,
  zIndex = 9997,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const effectsRef = useRef<RingEffect[]>([])
  const idRef = useRef(0)
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const resize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resize()
    window.addEventListener("resize", resize, { passive: true })

    const onClick = (e: MouseEvent) => {
      effectsRef.current.push({
        x: e.clientX,
        y: e.clientY,
        startTime: performance.now(),
        id: idRef.current++,
      })
    }
    window.addEventListener("click", onClick)

    const animate = (time: number) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Draw active rings and discard expired ones
      effectsRef.current = effectsRef.current.filter((effect) => {
        const elapsed = time - effect.startTime
        if (elapsed >= duration) return false

        const progress = elapsed / duration
        // Ease-out: fast expand then slow
        const eased = 1 - Math.pow(1 - progress, 3)
        const radius = (size / 2) * eased
        const alpha = 1 - progress

        ctx.beginPath()
        ctx.arc(effect.x, effect.y, radius, 0, Math.PI * 2)
        ctx.strokeStyle = color
        ctx.globalAlpha = alpha
        ctx.lineWidth = strokeWidth
        ctx.stroke()

        return true
      })

      ctx.globalAlpha = 1
      rafRef.current = requestAnimationFrame(animate)
    }

    rafRef.current = requestAnimationFrame(animate)

    return () => {
      window.removeEventListener("resize", resize)
      window.removeEventListener("click", onClick)
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    }
  }, [color, duration, size, strokeWidth])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        pointerEvents: "none",
        zIndex,
      }}
    />
  )
}
